import { useContext, useState, useEffect, useMemo } from 'react'
import { ModalContext } from '../ModalContext'
import { APIContext } from 'context/APIContext'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { FilterWorkoutByWorkoutLength } from 'functions/workoutUtils'

import WorkoutV5Set from 'components/Workout/WorkoutV5Set'
import OutlineButton from 'components/Buttons/OutlineButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function WorkoutV5CompletedSummaryModal() {

    const { modalData, HideModal } = useContext(ModalContext)
    const { RedoV5Workout } = useContext(APIContext)

    const { exerciseHashMap, exercises } = useGetExercises()

    // A workout trained at a selected length leaves out the slots outside it (mobility workouts have no selected length)
    const modalWorkout = modalData?.workout || null
    const selectedWorkoutLength = modalWorkout?.selectedWorkoutLength ?? null
    const workout = useMemo(() => (
        modalWorkout === null || selectedWorkoutLength === null ? modalWorkout : FilterWorkoutByWorkoutLength(modalWorkout, selectedWorkoutLength)
    ), [modalWorkout, selectedWorkoutLength])

    const [expandPhaseJSON, setExpandPhaseJSON] = useState(null)
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [deleteErrorMessage, setDeleteErrorMessage] = useState('')

    const InitializeExpandPhaseJSON = () => {
        var defaultExpandPhaseJSON = {}
        workout.phases.map((phase) => { defaultExpandPhaseJSON[phase.workoutPhaseId] = false })
        setExpandPhaseJSON(defaultExpandPhaseJSON)
    }

    const TogglePhaseExpand = (workoutPhaseId) => {
        var temp_expandPhaseJSON = JSON.parse(JSON.stringify(expandPhaseJSON))
        temp_expandPhaseJSON[workoutPhaseId] = !temp_expandPhaseJSON[workoutPhaseId]
        setExpandPhaseJSON(temp_expandPhaseJSON)
    }

    const DeleteWorkout = async () => {
        setIsDeleting(true)
        setDeleteErrorMessage('')

        const workoutDeleted = await RedoV5Workout(modalData.userId, workout.workoutId, modalData.clientDate)

        setIsDeleting(false)

        if (workoutDeleted === false) {
            setDeleteErrorMessage('There was an error deleting this workout. Please try again.')
            return
        }

        modalData.OnWorkoutDeleted?.()
        HideModal()
    }

    useEffect(() => { InitializeExpandPhaseJSON() }, [workout])

    if (
        Object.keys(exerciseHashMap).length === 0
        || exercises.length === 0
        || workout === null
        || expandPhaseJSON === null
    ) {
        return (
            <div className="pointer-events-none absolute inset-0 flex flex-col p-4 pt-8">
                <div className="pointer-events-auto min-h-0 flex-1 space-y-4 overflow-y-auto">
                    <div className="w-full h-20 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="w-full h-20 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="w-full h-20 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="w-full h-20 bg-gray-200 rounded-lg animate-pulse" />
                </div>
            </div>
        )
    }

    return (
        <div className="pointer-events-none absolute inset-0 p-4 pt-8">
            <div className="pointer-events-auto h-full space-y-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                <div className="text-center text-xl font-semibold leading-6 text-gray-900">
                    {workout.name}
                </div>

                {workout.phases.map((phase) => {
                    const isExpanded = expandPhaseJSON[phase.workoutPhaseId]

                    return (
                        <div 
                            key={phase.workoutPhaseId} 
                            className="rounded-xl overflow-hidden text-left"
                        >
                            <div 
                                className={`
                                    p-4 flex items-center bg-gray-100 cursor-pointer border-b
                                    transition-colors duration-200 ease-out
                                    ${isExpanded ? 'border-gray-300' : 'border-transparent'}
                                `} 
                                onClick={() => TogglePhaseExpand(phase.workoutPhaseId)}
                            >  
                                <p className="flex-grow">{phase.name}</p>

                                <ChevronDownIcon 
                                    className={`
                                        h-5 w-5 shrink-0
                                        transition-transform duration-200 ease-out
                                        ${isExpanded ? 'rotate-180' : 'rotate-0'}
                                    `} 
                                />
                            </div>

                            <div
                                className={`
                                    grid transition-[grid-template-rows] duration-300 ease-out
                                    ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
                                `}
                            >
                                <div className="overflow-hidden min-h-0">
                                    <div
                                        className={`
                                            divide-y divide-gray-300
                                            transition-opacity duration-200 ease-out
                                            ${isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                                        `}
                                    >
                                        {phase.type === 'circuit' && (
                                            <>
                                                {Array.from({ length: phase.sets }).map((_, setIndex) => (
                                                    <div 
                                                        key={setIndex}
                                                        className='divide-y divide-gray-300'
                                                    >
                                                        {phase.slots.map((workoutSlot) => (
                                                            <WorkoutV5Set 
                                                                key={workoutSlot.sets[setIndex]?.workoutSetId}
                                                                exerciseHashMap={exerciseHashMap}
                                                                setIndex={setIndex}
                                                                exerciseId={workoutSlot.exerciseId}
                                                                setsAssigned={phase.sets}
                                                                workoutSet={workoutSlot.sets[setIndex]} 
                                                            />
                                                        ))}
                                                    </div>
                                                ))}
                                            </>
                                        )}

                                        {phase.type === 'straight' && (
                                            <>
                                                {phase.slots.map((workoutSlot) => (
                                                    <div key={workoutSlot.workoutSlotId}>
                                                        {workoutSlot.sets.map((workoutSet, setIndex) => (
                                                            <WorkoutV5Set 
                                                                key={workoutSet.workoutSetId}
                                                                exerciseHashMap={exerciseHashMap}
                                                                setIndex={setIndex}
                                                                exerciseId={workoutSlot.exerciseId}
                                                                setsAssigned={workoutSlot.sets.length}
                                                                workoutSet={workoutSet}
                                                            />
                                                        ))}
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                })}

                {modalData?.isDeletable === true && !isConfirmingDelete && (
                    <div className='pt-2'>
                        <OutlineButton
                            text='Delete Workout'
                            action={() => setIsConfirmingDelete(true)}
                        />
                    </div>
                )}

                {modalData?.isDeletable === true && isConfirmingDelete && (
                    <div className='mt-2 p-4 space-y-3 rounded-xl border border-gray-300 text-center'>
                        <div className='text-lg font-semibold text-gray-900'>Delete Workout?</div>

                        <div className='space-y-1'>
                            <p className='text-sm text-gray-600'>Any progress saved as part of this workout will be removed.</p>
                            <p className='text-sm text-gray-600'>This action cannot be undone.</p>
                        </div>

                        {deleteErrorMessage !== '' && (
                            <p className='text-sm text-red-500'>{deleteErrorMessage}</p>
                        )}

                        <div className='flex items-center gap-x-2'>
                            <div className='flex-1'>
                                <OutlineButton
                                    text='Cancel'
                                    action={() => { setIsConfirmingDelete(false); setDeleteErrorMessage('') }}
                                />
                            </div>

                            <div className='flex-1'>
                                <BrandColorButton
                                    text='Delete'
                                    action={DeleteWorkout}
                                    isLoading={isDeleting}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
